import { FC, useEffect, useMemo, useState } from 'react';
import DayPicker, { PredefinedOption } from 'components/day-picker/DayPicker';
import { DownArrowIcon } from 'assets/icons';
import { useExpiryDateOptions } from 'containers/estimates/hooks/useExpiryDateOptions';
import { dateOptionMatcher } from 'containers/invoices/create-invoice/util';
import DateUtils from 'utils/dateUtils';
import {
  selectEstimateAutoFilledData,
  selectExpiryDate,
  selectRemindersEnabled,
  updateExpiryDate,
  updateRemindersEnabled
} from 'containers/estimates/estimatesSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { endOfDay } from 'date-fns';
import { EXPIRY_PERIOD_OPTIONS } from 'containers/estimates/constants';
import AutoFilledIndicator from 'components/auto-filled-indicator/AutoFilledIndicator';

const EstimateExpiry: FC = () => {
  const [isCalendarDisabled, setIsCalendarDisabled] = useState(false);
  const NoExpiryLabel = useMemo(
    () => EXPIRY_PERIOD_OPTIONS.find(option => option.id === 'no-expiry')?.btnLabel ?? '',
    []
  );

  const dispatch = useAppDispatch();
  const currentExpiryDate = useAppSelector(selectExpiryDate);
  const remindersEnabled = useAppSelector(selectRemindersEnabled);
  const autoFilledData = useAppSelector(selectEstimateAutoFilledData);

  // Determine if expiry date is autofilled
  let isExpiryDateAutoFilled = false;
  if (autoFilledData) {
    if (autoFilledData?.hasExpiry) {
      // If hasExpiry is true, check if dates match
      isExpiryDateAutoFilled =
        autoFilledData?.expiresAt && currentExpiryDate
          ? DateUtils.areSameDates(DateUtils.parseISODateString(autoFilledData.expiresAt), currentExpiryDate)
          : false;
    } else if (autoFilledData?.hasExpiry === false) {
      // If hasExpiry is false, check if currentExpiryDate is null (no expiry)
      isExpiryDateAutoFilled = currentExpiryDate === null;
    }
  }

  // Check if "No expiry" is selected (null expiry date)
  const isNoExpiry = !currentExpiryDate;

  // Display text logic
  let displayText = '';
  if (isNoExpiry) {
    displayText = NoExpiryLabel;
  } else if (currentExpiryDate) {
    const todayStart = DateUtils.getStartOfDay(DateUtils.getNow());
    const expiryEnd = DateUtils.getEndOfDay(currentExpiryDate);
    const diffDays = DateUtils.calculateDaysDifference(expiryEnd, todayStart);

    const formattedDate = DateUtils.getDateInFormat({
      date: currentExpiryDate,
      dateFormat: 'dd MMM yyyy'
    });

    if (diffDays === 0) {
      displayText = `Today (${formattedDate})`;
    } else {
      displayText = `In ${diffDays} ${diffDays > 1 ? 'days' : 'day'} (${formattedDate})`;
    }
  }

  useEffect(() => {
    // Disable calendar if "No expiry" is selected (null date)
    if (!currentExpiryDate) {
      setIsCalendarDisabled(true);
      // Disable reminders when no expiry date is set
      if (remindersEnabled) {
        dispatch(updateRemindersEnabled(false));
      }
    }
  }, [currentExpiryDate, remindersEnabled, dispatch]);

  // Use shared hook for predefined expiry date options
  const predefinedExpiryOptions = useExpiryDateOptions();

  // Handler for predefined option selection
  const onPredefinedOptionSelect = (option: PredefinedOption) => {
    // Handle "No expiry" option specially
    if (option.label === NoExpiryLabel) {
      dispatch(updateExpiryDate(null));
      setIsCalendarDisabled(true);
      // Disable reminders when no expiry date is set
      if (remindersEnabled) {
        dispatch(updateRemindersEnabled(false));
      }
    } else {
      setIsCalendarDisabled(false);
    }
  };

  const handleExpiryDateChange = (selectedDate: Date) => {
    dispatch(updateExpiryDate(endOfDay(selectedDate).toISOString()));
    setIsCalendarDisabled(false);
  };

  // Button trigger component
  const buttonNode = (
    <div className="flex h-10 w-[358px] items-center justify-between rounded bg-secondaryBtn px-5 text-17px">
      <div className={'flex items-center text-primaryText'}>{displayText}</div>
      <DownArrowIcon />
    </div>
  );

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="flex items-center whitespace-nowrap text-17px font-semibold text-primaryText">
        Expiry date
        <AutoFilledIndicator isAutoFilled={isExpiryDateAutoFilled} />
      </div>
      <div className="z-10 w-full max-w-md">
        <DayPicker
          date={currentExpiryDate}
          onSelect={handleExpiryDateChange}
          predefinedOptions={predefinedExpiryOptions}
          onPredefinedOptionSelect={onPredefinedOptionSelect}
          optionMatcher={dateOptionMatcher}
          showPastDates={false}
          className="w-full"
          labelClassName="text-17px font-semibold"
          customTrigger={buttonNode}
          disableCalendar={isCalendarDisabled}
          position="right"
          popoverPanelStyle={{
            minWidth: '406px',
            top: '0px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
          }}
        />
      </div>
    </div>
  );
};

export default EstimateExpiry;
